// src/app/shared/ui/GenerateBottomSheet.tsx
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus, MessageSquare, X } from "lucide-react";
import { hapticFeedback } from "@tma.js/sdk";
import { AnimatePresence, motion } from "motion/react";
import { useViewportInsets } from "@/app/hooks/useViewportInsets";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

const safeHaptic = (callback: () => void) => {
  try { callback(); } catch {}
};

interface GenerateBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GenerateBottomSheet({ isOpen, onClose }: GenerateBottomSheetProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { contentInsets } = useViewportInsets();

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleNewChat = () => {
    safeHaptic(() => hapticFeedback.impactOccurred("medium"));
    onClose();
    navigate("/text-generation");
  };

  const handleMyChats = () => {
    safeHaptic(() => hapticFeedback.impactOccurred("medium"));
    onClose();
    navigate("/text-generation", { state: { initialTab: 'chats' } });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t("createChat")}
            className="fixed bottom-0 left-0 right-0 z-[110] mx-auto w-full max-w-[var(--container-max-width,512px)]"
            style={{ paddingBottom: `${Math.max(contentInsets.bottom, 12)}px` }}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
           <div className="overflow-hidden rounded-t-3xl border border-white/30 dark:border-white/20 border-b-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl shadow-2xl">
              <div className="flex justify-center pt-3 pb-2">
                <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
              </div>

              {/* Заголовок и кнопка закрытия */}
              <div className="flex items-center justify-between px-10 py-4">
                <div>
                  <h2 className="text-xl font-bold items-center">{t("createChat")}</h2>
                  <p className="mt-0.5 text-sm text-muted-foreground items-center">{t("selectChatType")}</p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 dark:bg-white/10 text-foreground transition-colors hover:bg-white/30 dark:hover:bg-white/20 active:scale-90"
                  aria-label={t("close")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Кнопки выбора чата */}
              <div className="space-y-3 p-6 pb-8">
                <motion.button
                  type="button"
                  onClick={handleNewChat}
                  className="group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl p-4 text-left transition-all active:scale-95 hover:shadow-lg bg-white/20 dark:bg-white/10 backdrop-blur-md border border-white/30 dark:border-white/20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                >
                  <div
                    className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-lg"
                    style={{ background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }}
                  >
                    <MessageSquarePlus className="h-6 w-6" />
                  </div>
                  <div className="relative min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground">{t("newChat")}</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">{t("newChatDescription")}</p>
                  </div>
                  <span className="relative text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-accent">
                    →
                  </span>
                </motion.button>

                <motion.button
                  type="button"
                  onClick={handleMyChats}
                  className="group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl p-4 text-left transition-all active:scale-95 hover:shadow-lg bg-white/20 dark:bg-white/10 backdrop-blur-md border border-white/30 dark:border-white/20"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.16 }}
                >
                  <div
                    className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-lg"
                    style={{ background: "linear-gradient(135deg, #06b6d4, #2563eb)" }}
                  >
                    <MessageSquare className="h-6 w-6" />
                  </div>
                  <div className="relative min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground">{t("myChats")}</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">{t("myChatsDescriptionActive")}</p>
                  </div>
                  <span className="relative text-muted-foreground transition-all group-hover:translate-x-1 group-hover:text-accent">
                    →
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}